import { CaretRight, Check, Warning, X } from "@phosphor-icons/react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Member } from "~/api/types";
import { CONFIDENCE_PRECHECK } from "~/features/taskize/dedupe";
import {
  checkedCount,
  type DecisionRowModel,
  type ReminderRowModel,
  rowCount,
  type TaskizeRows,
  type TaskRowModel,
} from "~/features/taskize/rows";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";

/** Minutes before the due date. `null` is "No reminder" and is the default. */
const REMIND_LEADS: Array<{ minutes: number | null; label: string }> = [
  { minutes: null, label: "No reminder" },
  { minutes: 0, label: "At due time" },
  { minutes: 60, label: "1 hour before" },
  { minutes: 1440, label: "1 day before" },
  { minutes: 10080, label: "1 week before" },
];

export interface TaskizeTrayProps {
  rows: TaskizeRows;
  loading: boolean;
  error: string | null;
  thinkerName: string | null;
  members: Member[];
  /** v2 documents cannot take the decision block — sculpt refuses that lane (§C5/D3). */
  decisionsDisabled: boolean;
  busy: boolean;
  onPatch: (key: string, patch: Record<string, unknown>) => void;
  onConfirm: (keys: string[]) => void;
  onClose: () => void;
}

function Chip({ tone, children }: { tone: "muted" | "accent" | "warn"; children: string }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-small",
        tone === "accent" && "bg-accent text-accent-text",
        tone === "warn" && "bg-bg-secondary text-danger",
        tone === "muted" && "bg-bg-secondary text-text-tertiary",
      )}
    >
      {children}
    </span>
  );
}

function RowShell({
  checked,
  expanded,
  onToggleChecked,
  onToggleExpanded,
  checkboxLabel,
  disclosureLabel,
  confirmLabel,
  onConfirm,
  busy,
  right,
  main,
  detail,
}: {
  checked: boolean;
  expanded: boolean;
  onToggleChecked: () => void;
  onToggleExpanded: () => void;
  checkboxLabel: string;
  disclosureLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy: boolean;
  right: React.ReactNode;
  main: React.ReactNode;
  detail: React.ReactNode;
}) {
  return (
    <li className={cn("rounded-sm px-2 py-1.5", checked ? "opacity-100" : "opacity-55")}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          aria-label={checkboxLabel}
          onChange={onToggleChecked}
          className="size-4 shrink-0 accent-[var(--accent)]"
        />
        <button
          type="button"
          aria-label={disclosureLabel}
          aria-expanded={expanded}
          onClick={onToggleExpanded}
          className="flex size-5 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg"
        >
          <CaretRight
            size={12}
            aria-hidden
            className={cn("transition-transform", expanded ? "rotate-90" : "rtl:rotate-180")}
          />
        </button>
        {main}
        {right}
        <button
          type="button"
          aria-label={confirmLabel}
          onClick={onConfirm}
          disabled={busy}
          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg hover:text-accent disabled:opacity-40"
        >
          <Check size={14} aria-hidden />
        </button>
      </div>
      {expanded ? <div className="ms-8 mt-1 flex flex-col gap-1">{detail}</div> : null}
    </li>
  );
}

function Quote({ text }: { text: string | null }) {
  const { t } = useTranslation();
  if (!text) {
    return null;
  }
  return (
    <Text variant="small" className="border-divider border-s-2 ps-2 text-text-tertiary">
      {/* Static text. The server sends no anchor, so there is nothing to jump to (§0 C6). */}
      {t("“{{quote}}”", { quote: text })}
    </Text>
  );
}

function TaskRow({
  row,
  members,
  busy,
  onPatch,
  onConfirm,
}: {
  row: TaskRowModel;
  members: Member[];
  busy: boolean;
  onPatch: TaskizeTrayProps["onPatch"];
  onConfirm: (keys: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <RowShell
      checked={row.checked}
      expanded={row.expanded}
      busy={busy}
      onToggleChecked={() => onPatch(row.key, { checked: !row.checked })}
      onToggleExpanded={() => onPatch(row.key, { expanded: !row.expanded })}
      checkboxLabel={t("Include “{{title}}”", { title: row.title })}
      disclosureLabel={t("Show details for “{{title}}”", { title: row.title })}
      // §E6.4: kms disabled this while the row was unchecked, re-asking a question the
      // checkbox had already answered. Here it commits the row, whatever the checkbox says.
      confirmLabel={t("Create “{{title}}” now", { title: row.title })}
      onConfirm={() => onConfirm([row.key])}
      main={
        <input
          value={row.title}
          aria-label={t("Task title")}
          onChange={(event) => onPatch(row.key, { title: event.target.value })}
          className="min-w-0 flex-1 rounded-sm bg-transparent px-1 py-0.5 text-body text-text outline-none hover:bg-hover-bg focus:bg-bg focus:ring-1 focus:ring-input-border-focus"
        />
      }
      right={
        <>
          <select
            value={row.assigneeId ?? ""}
            aria-label={t("Assignee")}
            onChange={(event) => onPatch(row.key, { assigneeId: event.target.value || null })}
            className="h-7 max-w-36 shrink-0 rounded-sm border border-input-border bg-bg px-1.5 text-small text-text"
          >
            <option value="">
              {row.assigneeHint
                ? t("Unassigned ({{hint}})", { hint: row.assigneeHint })
                : t("Unassigned")}
            </option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={row.due}
            aria-label={t("Due")}
            title={row.dueHint ?? undefined}
            onChange={(event) => onPatch(row.key, { due: event.target.value })}
            className="h-7 shrink-0 rounded-sm border border-input-border bg-bg px-1.5 text-small text-text"
          />
          {row.due ? (
            <select
              value={row.remindLeadMinutes === null ? "" : String(row.remindLeadMinutes)}
              aria-label={t("Remind before due")}
              onChange={(event) =>
                onPatch(row.key, {
                  remindLeadMinutes: event.target.value === "" ? null : Number(event.target.value),
                })
              }
              className="h-7 shrink-0 rounded-sm border border-input-border bg-bg px-1.5 text-small text-text"
            >
              {REMIND_LEADS.map((lead) => (
                <option key={lead.label} value={lead.minutes === null ? "" : String(lead.minutes)}>
                  {t(lead.label)}
                </option>
              ))}
            </select>
          ) : null}
          {row.confidence < CONFIDENCE_PRECHECK ? <Chip tone="warn">{t("low")}</Chip> : null}
        </>
      }
      detail={
        <>
          {row.details ? <Text variant="small">{row.details}</Text> : null}
          <Quote text={row.sourceQuote} />
        </>
      }
    />
  );
}

function DecisionRow({
  row,
  busy,
  disabled,
  onPatch,
  onConfirm,
}: {
  row: DecisionRowModel;
  busy: boolean;
  disabled: boolean;
  onPatch: TaskizeTrayProps["onPatch"];
  onConfirm: (keys: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <RowShell
      checked={row.checked && !disabled}
      expanded={row.expanded}
      busy={busy || disabled}
      onToggleChecked={() => !disabled && onPatch(row.key, { checked: !row.checked })}
      onToggleExpanded={() => onPatch(row.key, { expanded: !row.expanded })}
      checkboxLabel={t("Include “{{title}}”", { title: row.statement })}
      disclosureLabel={t("Show details for “{{title}}”", { title: row.statement })}
      confirmLabel={t("Record “{{title}}” now", { title: row.statement })}
      onConfirm={() => !disabled && onConfirm([row.key])}
      main={
        <>
          <span aria-hidden>📌</span>
          <input
            value={row.statement}
            aria-label={t("Decision")}
            disabled={disabled}
            onChange={(event) => onPatch(row.key, { statement: event.target.value })}
            className="min-w-0 flex-1 rounded-sm bg-transparent px-1 py-0.5 text-body text-text outline-none hover:bg-hover-bg focus:bg-bg focus:ring-1 focus:ring-input-border-focus"
          />
        </>
      }
      right={
        <>
          {row.status ? (
            <Chip tone={row.status.toLowerCase() === "decided" ? "accent" : "muted"}>
              {t(row.status.toLowerCase() === "decided" ? "decided" : "proposed")}
            </Chip>
          ) : null}
          {row.dateHint ? <Chip tone="muted">{row.dateHint}</Chip> : null}
        </>
      }
      detail={<Quote text={row.sourceQuote} />}
    />
  );
}

function ReminderRow({
  row,
  busy,
  onPatch,
  onConfirm,
}: {
  row: ReminderRowModel;
  busy: boolean;
  onPatch: TaskizeTrayProps["onPatch"];
  onConfirm: (keys: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <RowShell
      checked={row.checked}
      expanded={row.expanded}
      busy={busy}
      onToggleChecked={() => onPatch(row.key, { checked: !row.checked })}
      onToggleExpanded={() => onPatch(row.key, { expanded: !row.expanded })}
      checkboxLabel={t("Include “{{title}}”", { title: row.title })}
      disclosureLabel={t("Show details for “{{title}}”", { title: row.title })}
      confirmLabel={t("Set “{{title}}” now", { title: row.title })}
      onConfirm={() => onConfirm([row.key])}
      main={
        <>
          <span aria-hidden>⏰</span>
          <input
            value={row.title}
            aria-label={t("Reminder")}
            onChange={(event) => onPatch(row.key, { title: event.target.value })}
            className="min-w-0 flex-1 rounded-sm bg-transparent px-1 py-0.5 text-body text-text outline-none hover:bg-hover-bg focus:bg-bg focus:ring-1 focus:ring-input-border-focus"
          />
        </>
      }
      right={<Chip tone="muted">{row.whenHint ?? t("pick a time")}</Chip>}
      detail={<Quote text={row.sourceQuote} />}
    />
  );
}

/**
 * The review tray — a Depth-2 bottom surface, not a modal.
 *
 * Locked decision **D2**: *Taskize always proposes.* Nothing is written until a person
 * confirms, which is why this is a tray you can read the document behind rather than a dialog
 * that covers it.
 */
export function TaskizeTray({
  rows,
  loading,
  error,
  thinkerName,
  members,
  decisionsDisabled,
  busy,
  onPatch,
  onConfirm,
  onClose,
}: TaskizeTrayProps) {
  const { t } = useTranslation();
  const total = rowCount(rows);
  const checked = checkedCount(rows);

  const guardedClose = () => {
    if (
      checked > 0 &&
      !window.confirm(t("Discard {{count}} unconfirmed items?", { count: checked }))
    ) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        guardedClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  });

  const summary = loading
    ? t("Reading the text…")
    : t("{{tasks}} tasks · {{decisions}} decisions · {{reminders}} reminders", {
        tasks: rows.tasks.length,
        decisions: rows.decisions.length,
        reminders: rows.reminders.length,
      });

  const allCheckedKeys = [...rows.tasks, ...rows.decisions, ...rows.reminders]
    .filter((row) => row.checked)
    .map((row) => row.key);

  return createPortal(
    <section
      aria-label={t("Taskize")}
      className="slate-pop fixed bottom-4 start-1/2 flex max-h-[60vh] w-[min(720px,100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-md border border-divider bg-menu-bg shadow-menu rtl:translate-x-1/2"
      style={{ zIndex: depths.dialog }}
    >
      <header className="flex items-center gap-2 border-b border-divider px-3 py-2">
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-accent" />
        <Text variant="body" className="shrink-0 font-semibold">
          {t("Taskize")}
        </Text>
        <Text variant="small" className="min-w-0 flex-1 truncate">
          {summary}
          {thinkerName ? ` · ${thinkerName}` : ""}
        </Text>
        <Button
          variant="primary"
          size="sm"
          disabled={checked === 0 || busy}
          onClick={() => onConfirm(allCheckedKeys)}
        >
          {t("Confirm all")}
          <span className="rounded-full bg-black/15 px-1.5 text-small">{checked}</span>
        </Button>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={guardedClose}
          className="flex size-7 shrink-0 items-center justify-center rounded-sm text-text-secondary hover:bg-hover-bg"
        >
          <X size={15} aria-hidden />
        </button>
      </header>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-2">
        {loading ? (
          <ul className="flex flex-col gap-2">
            {["a", "b", "c"].map((slot) => (
              <li key={slot} className="h-8 animate-pulse rounded-sm bg-bg-secondary" />
            ))}
          </ul>
        ) : error ? (
          <p className="flex items-center justify-center gap-2 py-6 text-center text-body text-danger">
            <Warning size={16} aria-hidden />
            {error}
          </p>
        ) : total === 0 ? (
          <Text variant="secondary" className="block py-6 text-center">
            {t("No tasks or decisions found in this text.")}
          </Text>
        ) : (
          <>
            {rows.tasks.length > 0 ? (
              <ul className="flex flex-col">
                {rows.tasks.map((row) => (
                  <TaskRow
                    key={row.key}
                    row={row}
                    members={members}
                    busy={busy}
                    onPatch={onPatch}
                    onConfirm={onConfirm}
                  />
                ))}
              </ul>
            ) : null}

            {rows.decisions.length > 0 ? (
              <>
                <Text
                  variant="small"
                  className="mt-2 block px-2 uppercase tracking-wide text-text-tertiary"
                >
                  {t("Decisions")}
                </Text>
                {decisionsDisabled ? (
                  <Text variant="small" className="block px-2 pb-1 text-text-tertiary">
                    {t(
                      "This document is on the new editor; drafting into it arrives with the new editor's AI dock.",
                    )}
                  </Text>
                ) : null}
                <ul className="flex flex-col">
                  {rows.decisions.map((row) => (
                    <DecisionRow
                      key={row.key}
                      row={row}
                      busy={busy}
                      disabled={decisionsDisabled}
                      onPatch={onPatch}
                      onConfirm={onConfirm}
                    />
                  ))}
                </ul>
              </>
            ) : null}

            {rows.reminders.length > 0 ? (
              <>
                <Text
                  variant="small"
                  className="mt-2 block px-2 uppercase tracking-wide text-text-tertiary"
                >
                  {t("Reminders")}
                </Text>
                <ul className="flex flex-col">
                  {rows.reminders.map((row) => (
                    <ReminderRow
                      key={row.key}
                      row={row}
                      busy={busy}
                      onPatch={onPatch}
                      onConfirm={onConfirm}
                    />
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </div>
    </section>,
    document.body,
  );
}
